/**
 * The Autotask IDs the owner chooses (BUILD_PLAN.md sections 10.2-10.4 and
 * 12.4; ADR 0019): queue, statuses, priority, issue types, billing codes and
 * note visibility, each picked by ID from the lists the nightly sync copied.
 *
 * Each choice saves as soon as it's made, as every mapping does: the field
 * keeps the new choice while it saves, then shows what's saved. A refusal is
 * shown under the field, which goes back to the saved choice, and the page
 * reloads the settings in case another device changed them.
 *
 * Nothing locks while a save is in flight (the review of rc.15): a change
 * made meanwhile is sent once it's answered, so saves never overlap. A
 * choice is sent after a short pause, or at once when its field is left, so
 * arrowing through a closed list (one change per key press on Windows)
 * saves only the choice it stops on.
 *
 * The API checks every choice again; this screen only offers valid ones.
 *
 * Above them is the owner's kill switch for writes to Autotask (BUILD_PLAN
 * 10.5 rule 6; #216). It can only make this environment's AUTOTASK_WRITE_MODE
 * stricter, saves as soon as it's chosen, and works before Autotask's first
 * sync: it needs none of the lists.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";

import { ApiError, retryUnlessRefused } from "../../api/client";
import type { AppSettings, AutotaskChoice, AutotaskChoices } from "../../api/types";
import { WRITE_MODE_TEXT } from "../../api/write-mode";
import { useSession } from "../../app/session-context";
import { Panel } from "../../components/ui/Panel";

type AutotaskKey = Extract<keyof AppSettings, `autotask_${string}`>;
type Draft = Partial<Record<AutotaskKey, number | null>>;

interface Field {
  key: AutotaskKey;
  label: string;
  hint: string;
  /** For a sub-issue type: the issue type setting it belongs to. */
  parent?: AutotaskKey;
}

interface Group {
  title: string;
  fields: Field[];
}

const GROUPS: readonly Group[] = [
  {
    title: "New tickets",
    fields: [
      { key: "autotask_queue_id", label: "Queue", hint: "IT Queue" },
      {
        key: "autotask_status_in_progress_id",
        label: "Status when created",
        hint: "In Progress",
      },
      { key: "autotask_status_complete_id", label: "Status when closed", hint: "Complete" },
      { key: "autotask_priority_id", label: "Priority", hint: "The default for new tickets" },
    ],
  },
  {
    title: "Managed clients",
    fields: [
      { key: "autotask_issue_type_managed_id", label: "Issue type", hint: "For managed work" },
      {
        key: "autotask_sub_issue_type_managed_id",
        label: "Sub-issue type",
        hint: "Of the issue type above",
        parent: "autotask_issue_type_managed_id",
      },
    ],
  },
  {
    title: "Hourly clients",
    fields: [
      { key: "autotask_issue_type_hourly_id", label: "Issue type", hint: "For hourly work" },
      {
        key: "autotask_sub_issue_type_hourly_id",
        label: "Sub-issue type",
        hint: "Of the issue type above",
        parent: "autotask_issue_type_hourly_id",
      },
    ],
  },
  {
    title: "Billing codes",
    fields: [
      { key: "autotask_billing_code_managed_id", label: "Managed work", hint: "S-Flat" },
      {
        key: "autotask_billing_code_hourly_remote_id",
        label: "Hourly, remote",
        hint: "C-Hourly Remote",
      },
      {
        key: "autotask_billing_code_hourly_onsite_id",
        label: "Hourly, on-site",
        hint: "C-Hourly On-site",
      },
    ],
  },
  {
    title: "Ticket notes",
    fields: [
      {
        key: "autotask_note_publish_id",
        label: "Who can see the activity note",
        hint: "Internal only is recommended",
      },
      {
        key: "autotask_note_type_id",
        label: "Note type",
        hint: "The kind of note Autotask files it as. Autotask requires one",
      },
    ],
  },
];

const FIELDS: readonly Field[] = GROUPS.flatMap((group) => group.fields);

const selectClass =
  "h-10 w-full rounded-input border border-line bg-card px-3 text-text " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand " +
  "disabled:cursor-not-allowed disabled:opacity-60";

function optionLabel(choice: AutotaskChoice): string {
  return `${choice.label} · ${String(choice.id)}`;
}

function SettingSelect({
  field,
  value,
  choices,
  parentValue,
  saved,
  error,
  status,
  onChange,
  onLeave,
}: {
  field: Field;
  value: number | null;
  /** What the server has: still offered, flagged, while another is shown. */
  saved: number | null;
  choices: readonly AutotaskChoice[];
  parentValue: number | null | undefined;
  error: string | undefined;
  /** "Saving…" or "Saved.", for the field just changed. */
  status: string | undefined;
  onChange: (value: number | null) => void;
  onLeave: () => void;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const needsParent = field.parent !== undefined && parentValue === null;
  const offered = choices.filter(
    (c) => c.available && (field.parent === undefined || c.parent_id === parentValue),
  );
  const current = value === null ? undefined : choices.find((c) => c.id === value);
  const currentUsable = current !== undefined && offered.includes(current);
  const usable = (id: number) => offered.some((c) => c.id === id);
  // Flagged choices to offer: the one shown, and the saved one, so arrowing
  // away from a saved choice that can't be used can come back to it.
  const flagged = [...new Set([value, saved])].filter(
    (id): id is number => id !== null && !usable(id),
  );

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="font-medium">
        {field.label}
      </label>
      <select
        id={id}
        aria-describedby={hintId}
        aria-invalid={error !== undefined || (value !== null && !currentUsable)}
        className={selectClass}
        value={value === null ? "" : String(value)}
        disabled={needsParent}
        onChange={(event) => {
          onChange(event.target.value === "" ? null : Number(event.target.value));
        }}
        onBlur={onLeave}
      >
        <option value="">Not chosen</option>
        {flagged.map((id) => {
          const known = choices.find((c) => c.id === id);
          return (
            <option key={`flagged-${String(id)}`} value={String(id)}>
              {known ? `${optionLabel(known)} (can't be used)` : `${String(id)} (not in Autotask)`}
            </option>
          );
        })}
        {offered.map((choice) => (
          <option key={choice.id} value={String(choice.id)}>
            {optionLabel(choice)}
          </option>
        ))}
      </select>
      <p id={hintId} className="text-sm text-muted">
        {needsParent ? "Choose the issue type first." : field.hint}
      </p>
      <p role="status" className="text-sm font-medium text-status-logged-fg empty:hidden">
        {status}
      </p>
      {value !== null && !currentUsable && error === undefined ? (
        <p className="rounded-row bg-status-needs-client-bg px-3 py-1 text-sm text-status-needs-client-fg">
          Needs attention: this choice is inactive or no longer in Autotask. Choose another.
        </p>
      ) : null}
      {error !== undefined ? (
        <p role="alert" className="rounded-row bg-status-needs-client-bg px-3 py-1 text-sm text-status-needs-client-fg">
          Not saved: {error}
        </p>
      ) : null}
    </div>
  );
}

function formatSynced(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: timezone,
  }).format(new Date(iso));
}

/** How long a choice waits for the next one before it's sent. */
const PAUSE_MS = 400;

type Errors = Partial<Record<AutotaskKey, string>>;

function without<T extends object>(values: T, keys: readonly string[]): T {
  return Object.fromEntries(Object.entries(values).filter(([key]) => !keys.includes(key))) as T;
}

function AutotaskSettingsForm({
  settings,
  choices,
}: {
  settings: AppSettings;
  choices: AutotaskChoices;
}) {
  const { api, me } = useSession();
  const queryClient = useQueryClient();
  // What the owner chose that the server hasn't confirmed: shown in its field
  // ("Saving…") until it does. The ref is the same, for the handlers.
  const [chosen, setChosenState] = useState<Draft>({});
  const chosenRef = useRef<Draft>({});
  // Why a choice wasn't saved, under its field, until that field changes or saves.
  const [errors, setErrors] = useState<Errors>({});
  // The field "Saved." shows under: the last one the owner touched, once confirmed.
  const [lastSaved, setLastSaved] = useState<AutotaskKey | null>(null);
  const lastTouched = useRef<AutotaskKey | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // The save in flight: the next one is sent after it, never alongside it.
  const inFlight = useRef<Promise<void> | null>(null);
  const mounted = useRef(false);
  const leave = useRef<() => void>(() => undefined);
  const flushRef = useRef<() => void>(() => undefined);

  function setChosen(next: Draft) {
    chosenRef.current = next;
    setChosenState(next);
  }

  const shown = (key: AutotaskKey): number | null =>
    key in chosen ? (chosen[key] ?? null) : (settings[key] ?? null);

  /** What a field shows: a sub-issue type that doesn't belong to a newly
   * chosen issue type shows as cleared, and is cleared when it's sent. */
  function valueOf(key: AutotaskKey): number | null {
    const own = shown(key);
    const parent = FIELDS.find((f) => f.key === key)?.parent;
    if (parent === undefined || own === null || !(parent in chosen) || shown(parent) === settings[parent]) {
      return own;
    }
    const fits = choices.options[key]?.some((c) => c.id === own && c.parent_id === shown(parent));
    return fits ? own : null;
  }

  /** What to send now: the chosen values the server doesn't have, worked out
   * against the final choices, so arrowing away and back sends nothing, and a
   * sub-issue type is cleared only if the issue type it's sent with needs it. */
  function batchToSend(): Draft {
    const server = queryClient.getQueryData<AppSettings>(["settings"]) ?? settings;
    const want = chosenRef.current;
    const value = (key: AutotaskKey): number | null =>
      key in want ? (want[key] ?? null) : (server[key] ?? null);
    const batch: Draft = {};
    for (const field of FIELDS) {
      let next = value(field.key);
      if (field.parent !== undefined && next !== null && value(field.parent) !== server[field.parent]) {
        const parent = value(field.parent);
        const fits = choices.options[field.key]?.some((c) => c.id === next && c.parent_id === parent);
        next = fits ? next : null;
      }
      if (next !== (server[field.key] ?? null)) {
        batch[field.key] = next;
      }
    }
    return batch;
  }

  /** Send what's chosen, unless a save is in flight (it's sent when that's answered). */
  function flush() {
    clearTimeout(timer.current);
    timer.current = undefined;
    if (inFlight.current !== null) {
      return;
    }
    const batch = batchToSend();
    // What the server already has needs no save; the rest shows "Saving…".
    setChosen(batch);
    const keys = Object.keys(batch);
    if (keys.length === 0) {
      return;
    }
    const confirmed = () => {
      // Done with what was sent, unless the owner chose again since.
      const now = chosenRef.current;
      setChosen(
        Object.fromEntries(
          Object.entries(now).filter(([key, v]) => !(key in batch) || batch[key as AutotaskKey] !== v),
        ),
      );
    };
    inFlight.current = api
      .updateSettings(batch)
      .then(
        (result) => {
          queryClient.setQueryData(["settings"], result);
          confirmed();
          setErrors((old) => without(old, keys));
          const touched = lastTouched.current;
          setLastSaved(touched !== null && touched in batch ? touched : (keys[0] as AutotaskKey));
        },
        (error: unknown) => {
          const fieldErrors = error instanceof ApiError ? error.fieldErrors : [];
          // The refused choices go back to what's saved, with the reason under
          // them. If the refusal names some of the choices sent, the others
          // stay chosen and are sent again; otherwise all go back.
          const refused = fieldErrors.map((e) => e.field).filter((key) => key in batch);
          const back = refused.length > 0 ? refused : keys;
          // A sub-issue type sent with a refused issue type goes back with it:
          // it was cleared, or chosen, for the issue type that was refused.
          for (const field of FIELDS) {
            if (field.parent !== undefined && back.includes(field.parent) && field.key in batch) {
              back.push(field.key);
            }
          }
          // A field chosen again since this was sent keeps its new choice, and
          // the reason for the old one isn't shown under it.
          const chosenAgain = (key: string) =>
            key in chosenRef.current && chosenRef.current[key as AutotaskKey] !== batch[key as AutotaskKey];
          setChosen(
            Object.fromEntries(
              Object.entries(chosenRef.current).filter(
                ([key, v]) => !(back.includes(key) && batch[key as AutotaskKey] === v),
              ),
            ),
          );
          const failure = error instanceof Error ? error.message : "Something went wrong.";
          const pairs: [string, string][] =
            fieldErrors.length > 0
              ? fieldErrors.map((e): [string, string] => [e.field, e.message])
              : keys.map((key): [string, string] => [key, failure]);
          const reasons: Errors = Object.fromEntries(pairs.filter(([key]) => !chosenAgain(key)));
          setErrors((old) => ({ ...old, ...reasons }));
          // A refusal may come from a change made elsewhere: show what the
          // server has, before the next save is sent (so a late reload can't
          // undo it). A failure to reach it leaves the form as it is.
          if (error instanceof ApiError && error.status < 500) {
            return queryClient.invalidateQueries({ queryKey: ["settings"] });
          }
          return undefined;
        },
      )
      .finally(() => {
        inFlight.current = null;
        // What was chosen meanwhile goes next, unless the owner is still choosing.
        if (mounted.current && timer.current === undefined) {
          flush();
        }
      });
  }

  useEffect(() => {
    flushRef.current = flush;
    leave.current = () => {
      clearTimeout(timer.current);
      // Leaving the page: what's chosen goes after the save in flight, never
      // alongside it. A refusal can't be shown (the page is gone): the
      // settings are reloaded, so the page shows what's saved next time.
      void (inFlight.current ?? Promise.resolve()).then(async () => {
        const batch = batchToSend();
        if (Object.keys(batch).length === 0) {
          return;
        }
        try {
          queryClient.setQueryData(["settings"], await api.updateSettings(batch));
        } catch {
          await queryClient.invalidateQueries({ queryKey: ["settings"] });
        }
      });
    };
  });

  useEffect(() => {
    // Closing or reloading the tab with a choice still to save: ask first.
    // Hiding it (switching tabs, locking the screen): send it now.
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (Object.keys(chosenRef.current).length > 0 || inFlight.current !== null) {
        event.preventDefault();
      }
    };
    const hidden = () => {
      if (document.visibilityState === "hidden") {
        flushRef.current();
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("visibilitychange", hidden);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("visibilitychange", hidden);
    };
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      leave.current();
    };
  }, []);

  function choose(field: Field, value: number | null) {
    setChosen({ ...chosenRef.current, [field.key]: value });
    lastTouched.current = field.key;
    setLastSaved(null);
    setErrors((old) => without(old, [field.key]));
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, PAUSE_MS);
  }

  function statusOf(key: AutotaskKey): string | undefined {
    if (key in chosen) {
      return "Saving…";
    }
    return key === lastSaved ? "Saved." : undefined;
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="text-muted">
        {choices.synced_at
          ? `Lists from the Autotask sync of ${formatSynced(choices.synced_at, me.timezone)}.`
          : null}{" "}
        Choices are stored by Autotask ID, never by name, and each one saves as soon as you make
        it.
      </p>
      {GROUPS.map((group) => (
        <fieldset key={group.title} className="rounded-card border border-line bg-panel-2 p-4">
          <legend className="px-1 text-[12.5px] font-medium uppercase tracking-[0.08em] text-muted">
            {group.title}
          </legend>
          <div className="grid gap-4 md:grid-cols-2">
            {group.fields.map((field) => (
              <SettingSelect
                key={field.key}
                field={field}
                value={valueOf(field.key)}
                saved={settings[field.key] ?? null}
                choices={choices.options[field.key] ?? []}
                parentValue={field.parent ? valueOf(field.parent) : undefined}
                error={errors[field.key]}
                status={statusOf(field.key)}
                onChange={(value) => {
                  choose(field, value);
                }}
                onLeave={flush}
              />
            ))}
          </div>
        </fieldset>
      ))}
    </div>
  );
}

type Override = NonNullable<AppSettings["write_mode_override"]> | null;

const OVERRIDES: readonly { value: Override; label: string }[] = [
  { value: null, label: "As this environment allows" },
  { value: "allowlist", label: "Allowlist only" },
  { value: "off", label: "Off" },
];

/** The kill switch: one choice, saved as soon as it's made. A choice made
 * while one saves is sent once that's answered, never alongside it. */
function WriteModeSwitch({ settings }: { settings: AppSettings }) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const id = useId();
  const integrations = useQuery({
    queryKey: ["integrations"],
    queryFn: () => api.getIntegrations(),
    retry: retryUnlessRefused,
  });
  // The choice not yet confirmed, shown while it saves.
  const [chosen, setChosen] = useState<{ value: Override } | null>(null);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const next = useRef<{ value: Override } | null>(null);
  const saving = useRef(false);

  const saved = settings.write_mode_override ?? null;
  const value = chosen === null ? saved : chosen.value;
  const autotask = integrations.data?.autotask;
  // "Allowlist only" changes nothing where this environment allows less.
  const offered = OVERRIDES.filter(
    (o) => o.value !== "allowlist" || autotask?.allowed_write_mode === "on" || o.value === saved,
  );

  function send() {
    const choice = next.current;
    if (saving.current || choice === null) {
      return;
    }
    next.current = null;
    saving.current = true;
    api
      .updateSettings({ write_mode_override: choice.value })
      .then(
        (result) => {
          queryClient.setQueryData(["settings"], result);
          setError(undefined);
          setStatus("Saved.");
          return queryClient.invalidateQueries({ queryKey: ["integrations"] });
        },
        (failure: unknown) => {
          setError(failure instanceof Error ? failure.message : "Something went wrong.");
          setStatus(undefined);
          if (failure instanceof ApiError && failure.status < 500) {
            return queryClient.invalidateQueries({ queryKey: ["settings"] });
          }
          return undefined;
        },
      )
      .finally(() => {
        saving.current = false;
        if (next.current === null) {
          setChosen(null);
        } else {
          send();
        }
      });
  }

  return (
    <fieldset className="rounded-card border border-line bg-panel-2 p-4">
      <legend className="px-1 text-[12.5px] font-medium uppercase tracking-[0.08em] text-muted">
        Writes to Autotask
      </legend>
      <div className="flex flex-col gap-1 md:w-1/2 md:pr-2">
        <label htmlFor={id} className="font-medium">
          Writes to Autotask
        </label>
        <select
          id={id}
          aria-describedby={`${id}-now ${id}-hint`}
          aria-invalid={error !== undefined}
          className={selectClass}
          value={value ?? ""}
          onChange={(event) => {
            const picked = OVERRIDES.find((o) => (o.value ?? "") === event.target.value);
            if (picked === undefined) {
              return;
            }
            setChosen({ value: picked.value });
            setStatus("Saving…");
            setError(undefined);
            next.current = { value: picked.value };
            send();
          }}
        >
          {offered.map((o) => (
            <option key={o.label} value={o.value ?? ""}>
              {o.label}
            </option>
          ))}
        </select>
        <p id={`${id}-now`} className="text-sm font-medium empty:hidden">
          {autotask ? `Now: ${WRITE_MODE_TEXT[autotask.write_mode]}` : null}
        </p>
        <p id={`${id}-hint`} className="text-sm text-muted">
          Off stops every write at once. This can only make the environment&apos;s own setting
          stricter, never looser.
        </p>
        <p role="status" className="text-sm font-medium text-status-logged-fg empty:hidden">
          {status}
        </p>
        {error !== undefined ? (
          <p role="alert" className="rounded-row bg-status-needs-client-bg px-3 py-1 text-sm text-status-needs-client-fg">
            Not saved: {error}
          </p>
        ) : null}
      </div>
    </fieldset>
  );
}

export function AutotaskSettings() {
  const { api } = useSession();
  const settings = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.getSettings(),
    retry: retryUnlessRefused,
  });
  const choices = useQuery({
    queryKey: ["autotask-choices"],
    queryFn: () => api.getAutotaskChoices(),
    retry: retryUnlessRefused,
  });

  let body;
  // A failed reload keeps the form it already has (and any refusal in it):
  // only a first load that fails has nothing to show.
  if ((settings.error && !settings.data) || (choices.error && !choices.data)) {
    body = (
      <p role="alert" className="text-muted">
        The Autotask settings could not be loaded. Reload the page to try again.
      </p>
    );
  } else if (!settings.data || !choices.data) {
    body = (
      <p role="status" className="text-muted">
        Loading…
      </p>
    );
  } else if (choices.data.synced_at === null) {
    body = (
      <p className="text-muted">
        Autotask hasn&apos;t been synced yet, so there is nothing to choose from. The sync runs
        every night; Owner tools → Autotask → Sync Autotask now runs it now.
      </p>
    );
  } else {
    body = <AutotaskSettingsForm settings={settings.data} choices={choices.data} />;
  }

  return (
    <Panel aria-labelledby="autotask-settings-heading">
      <h2 id="autotask-settings-heading" className="text-lg font-semibold tracking-tight">
        Autotask settings
      </h2>
      <div className="mt-3 flex flex-col gap-5">
        {settings.data ? <WriteModeSwitch settings={settings.data} /> : null}
        {body}
      </div>
    </Panel>
  );
}
